import React, { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { PageHeader, Card } from "@/components/Primitives";
import FilterBar from "@/components/FilterBar";
import ChartBlock from "@/components/ChartBlock";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BookmarkSimple, FloppyDisk, Trash } from "@phosphor-icons/react";

const qs = (f) => {
  const p = new URLSearchParams();
  Object.entries(f || {}).forEach(([k, v]) => { if (v) p.append(k, v); });
  const s = p.toString();
  return s ? `?${s}` : "";
};

const PRESETS = [
  { k: "last_7", l: "Last 7 Days" },
  { k: "mtd", l: "MTD" },
  { k: "qtd", l: "Quarter" },
  { k: "ytd", l: "YTD" },
];

const Reports = () => {
  const [filters, setFilters] = useState({ preset: "ytd" });
  const [tab, setTab] = useState("performance");
  const [kpi, setKpi] = useState(null);
  const [yoy, setYoY] = useState([]);
  const [brandYoy, setBrandYoy] = useState([]);
  const [brands, setBrands] = useState([]);
  const [locYoy, setLocYoy] = useState([]);
  const [salesmenYoy, setSalesmenYoy] = useState([]);
  const [topSkus, setTopSkus] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState("");
  const [salesmanByLoc, setSalesmanByLoc] = useState([]);
  const query = useMemo(() => qs(filters), [filters]);

  // ── Saved presets ──────────────────────────────────────────
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [presetMenu, setPresetMenu] = useState(false);

  const loadPresets = () => api.get("/presets").then((r) => setSavedPresets(r.data || []));
  useEffect(() => { loadPresets(); }, []);

  const savePreset = async () => {
    const name = (presetName || "").trim();
    if (!name) { toast.error("Name your preset first"); return; }
    try {
      await api.post("/presets", { name, filters, tab });
      toast.success(`Preset “${name}” saved`);
      setPresetName(""); setPresetMenu(false);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const applyPreset = (p) => {
    setFilters(p.filters || {});
    if (p.tab) setTab(p.tab);
    setPresetMenu(false);
    toast.success(`Loaded “${p.name}”`);
  };

  const deletePreset = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/presets/${id}`);
      toast.success("Preset deleted");
      loadPresets();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  useEffect(() => {
    api.get(`/analytics/kpis${query}`).then((r) => setKpi(r.data));
    api.get(`/analytics/yoy${query}`).then((r) => setYoY(r.data.rows || []));
    api.get(`/analytics/brands${query}`).then((r) => setBrands(r.data.rows || []));
    api.get(`/analytics/top-skus?${query ? query.slice(1) + "&" : ""}limit=15`).then((r) => setTopSkus(r.data.rows || []));
  }, [query]);

  useEffect(() => {
    api.get("/analytics/brand-yoy").then((r) => setBrandYoy(r.data.rows || []));
    api.get("/analytics/locations-yoy").then((r) => setLocYoy(r.data.rows || []));
    const locQ = selectedLoc ? `&location_code=${selectedLoc}` : "";
    api.get(`/analytics/salesmen-yoy?limit=25${locQ}`).then((r) => setSalesmenYoy(r.data.rows || []));
    api.get("/ref/locations").then((r) => {
      setLocations(r.data.rows || []);
      if (r.data.rows?.length && !selectedLoc) setSelectedLoc(r.data.rows[0].code);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoc]);

  useEffect(() => {
    if (!selectedLoc) return;
    api.get(`/analytics/salesman-by-location?location_code=${selectedLoc}&limit=15${query ? "&" + query.slice(1) : ""}`)
      .then((r) => setSalesmanByLoc(r.data.rows || []));
  }, [selectedLoc, query]);

  const yoyYears = Array.from(new Set(yoy.flatMap((r) => Object.keys(r).filter((k) => k !== "month")))).sort();
  const brandYears = Array.from(new Set(brandYoy.flatMap((r) => Object.keys(r).filter((k) => k !== "brand")))).sort();

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="The storytelling ledger"
        subtitle="Performance, comparisons, and rankings for the House — Last 7 days, MTD, Quarter, YTD. Flip between brands, locations, salesmen. All KWD."
      />

      <div className="flex flex-wrap gap-2 mb-4 no-print items-center">
        {PRESETS.map((p) => (
          <button
            key={p.k}
            data-testid={`report-preset-${p.k}`}
            onClick={() => setFilters({ ...filters, preset: p.k, start_date: "", end_date: "" })}
            className={`text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-sm border transition-all font-semibold ${
              filters.preset === p.k ? "border-myb-500 text-white bg-myb-500" : "border-myb-200 text-muted-foreground hover:text-myb-500 hover:border-myb-500"
            }`}
          >
            {p.l}
          </button>
        ))}

        {/* ── Saved presets ───────────────────────────────────── */}
        <div className="relative ml-auto">
          <button
            data-testid="preset-menu-toggle"
            onClick={() => setPresetMenu((v) => !v)}
            className="text-xs uppercase tracking-[0.2em] px-3 py-2 rounded-sm border border-myb-200 text-muted-foreground hover:text-myb-500 hover:border-myb-500 font-semibold inline-flex items-center gap-2"
          >
            <BookmarkSimple size={13} weight="duotone" />
            Saved views {savedPresets.length ? `· ${savedPresets.length}` : ""}
          </button>
          {presetMenu && (
            <div
              data-testid="preset-menu"
              className="absolute right-0 mt-2 w-80 bg-white border hairline rounded-sm shadow-xl z-30 p-3"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-myb-500 font-semibold mb-2">Save current view</div>
              <div className="flex gap-2">
                <input
                  data-testid="preset-name-input"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") savePreset(); }}
                  placeholder="e.g. Q1 Cartier Focus"
                  className="flex-1 bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2 text-xs outline-none"
                />
                <button
                  data-testid="preset-save-btn"
                  onClick={savePreset}
                  className="text-[11px] uppercase tracking-wider px-3 py-2 bg-myb-500 text-white hover:bg-myb-600 rounded-sm font-semibold inline-flex items-center gap-1"
                >
                  <FloppyDisk size={12} /> Save
                </button>
              </div>
              <div className="border-t hairline my-3" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-myb-500 font-semibold mb-1.5">Your views</div>
              {savedPresets.length === 0 ? (
                <div className="text-xs text-muted-foreground italic py-4 text-center">No saved views yet. Name the current filter set and hit Save.</div>
              ) : (
                <div className="max-h-56 overflow-auto scrollbar-thin space-y-1">
                  {savedPresets.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`preset-load-${p.id}`}
                      onClick={() => applyPreset(p)}
                      className="w-full group flex items-center justify-between gap-2 px-2.5 py-2 rounded-sm border hairline hover:border-myb-500 hover:bg-myb-50/40 transition-all text-left"
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">{p.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {p.tab || "performance"} · {Object.entries(p.filters || {}).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ") || "no filters"}
                        </div>
                      </div>
                      <span
                        role="button"
                        data-testid={`preset-delete-${p.id}`}
                        onClick={(e) => deletePreset(p.id, e)}
                        className="text-rose-500/80 hover:text-rose-600 p-1 opacity-0 group-hover:opacity-100 transition"
                        title="Delete"
                      ><Trash size={13} /></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <FilterBar value={filters} onChange={setFilters} />

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white border hairline grid grid-cols-3 md:grid-cols-6 w-full lg:w-fit">
          <TabsTrigger data-testid="report-tab-performance" value="performance">Performance</TabsTrigger>
          <TabsTrigger data-testid="report-tab-yoy" value="yoy">YoY</TabsTrigger>
          <TabsTrigger data-testid="report-tab-brands" value="brands">Brands</TabsTrigger>
          <TabsTrigger data-testid="report-tab-locations" value="locations">Locations</TabsTrigger>
          <TabsTrigger data-testid="report-tab-salesmen" value="salesmen">Salesmen</TabsTrigger>
          <TabsTrigger data-testid="report-tab-skus" value="skus">SKUs</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="mt-5 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
            <Card title="Net Sales" testId="kpi-net-sales">
              <div className="font-display text-4xl text-myb-500 font-light">{fmtNumber(kpi?.total_sales)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1">KWD · current period</div>
            </Card>
            <Card title="Transactions" testId="kpi-transactions">
              <div className="font-display text-4xl text-myb-500 font-light">{fmtNumber(kpi?.transactions)}</div>
              {kpi?.prior_year_transactions != null && (
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1">
                  vs {fmtNumber(kpi.prior_year_transactions)} LY
                </div>
              )}
            </Card>
            <Card title="Avg Ticket" testId="kpi-avg-ticket">
              <div className="font-display text-4xl text-myb-500 font-light">{fmtNumber(kpi?.avg_ticket)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1">KWD</div>
            </Card>
            <Card title="Gross Profit" testId="kpi-gross-profit">
              <div className="font-display text-4xl text-myb-500 font-light">{fmtNumber(kpi?.gross_profit)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1">KWD · margin {(kpi?.gp_margin_pct ?? 0).toFixed(1)}%</div>
            </Card>
            <Card title="Discount" testId="kpi-discount">
              <div className="font-display text-4xl text-myb-500 font-light">{fmtNumber(kpi?.discount)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1">KWD · {(kpi?.discount_pct ?? 0).toFixed(1)}% of gross</div>
            </Card>
            <Card title="Last Year Sales" testId="kpi-prior-year-sales">
              <div className="font-display text-4xl text-myb-500/80 font-light">{fmtNumber(kpi?.prior_year_sales)}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1">KWD · same window · prior year</div>
            </Card>
            <Card title="Growth %" testId="kpi-growth">
              <div className={`font-display text-4xl font-light ${
                kpi?.growth_pct == null ? "text-myb-500" : kpi.growth_pct >= 0 ? "text-emerald-600" : "text-rose-600"
              }`}>
                {kpi?.growth_pct != null ? `${kpi.growth_pct >= 0 ? "▲" : "▼"} ${Math.abs(kpi.growth_pct).toFixed(1)}%` : "—"}
              </div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mt-1 leading-snug">
                {kpi?.prior_year_sales != null
                  ? <>vs <span className="text-myb-500 font-bold">{fmtNumber(kpi.prior_year_sales)} KWD</span> last year</>
                  : "No prior-year baseline"}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="yoy" className="mt-5">
          <Card testId="yoy-chart" title="Monthly sales — current vs prior year">
            <ChartBlock spec={{ kind: "line", x_key: "month", y_keys: yoyYears }} data={yoy} height={340} />
          </Card>
        </TabsContent>

        <TabsContent value="brands" className="mt-5 space-y-6">
          <Card testId="brand-yoy-chart" title="Brand sales YoY (full calendar)">
            <ChartBlock spec={{ kind: "bar", x_key: "brand", y_keys: brandYears }} data={brandYoy.slice(0, 12)} height={340} />
          </Card>
          <Card testId="brands-table" title="Brand contribution — filtered period">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b hairline">
                  {["Brand", "Net Sales (KWD)", "Gross Profit (KWD)", "Qty"].map((h) => (
                    <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {brands.map((r) => (
                  <tr key={r.brand} className="border-b hairline hover:bg-myb-50/50">
                    <td className="py-2 px-3 font-medium">{r.brand}</td>
                    <td className="py-2 px-3">{fmtNumber(r.net_value)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{fmtNumber(r.gross_profit)}</td>
                    <td className="py-2 px-3 text-muted-foreground">{fmtNumber(r.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="locations" className="mt-5 space-y-6">
          <Card testId="location-avg-ticket-yoy" title="Avg ticket per location — 2025 vs 2026">
            <div className="overflow-auto max-h-[500px] scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/95">
                  <tr className="text-left">
                    {["Location", "Avg 2025 (KWD)", "Avg 2026 (KWD)", "Δ %", "Net 2026"].map((h) => (
                      <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold border-b hairline">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {locYoy.map((r) => {
                    const a25 = r.avg_ticket_2025 || 0;
                    const a26 = r.avg_ticket_2026 || 0;
                    const delta = a25 ? ((a26 - a25) / a25 * 100) : null;
                    return (
                      <tr key={r.location_code} className="border-b hairline hover:bg-myb-50/50">
                        <td className="py-2 px-3">{r.location_name}</td>
                        <td className="py-2 px-3 text-muted-foreground">{a25 ? fmtNumber(a25) : "—"}</td>
                        <td className="py-2 px-3 font-medium">{a26 ? fmtNumber(a26) : "—"}</td>
                        <td className={`py-2 px-3 text-xs font-medium ${delta == null ? "text-muted-foreground" : delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {delta == null ? "—" : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta).toFixed(1)}%`}
                        </td>
                        <td className="py-2 px-3 text-myb-500">{r.net_value_2026 ? fmtNumber(r.net_value_2026) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="salesmen" className="mt-5 space-y-6">
          <Card testId="salesman-by-location" title="Salesman contribution — per location"
                right={
                  <select
                    data-testid="salesman-loc-picker"
                    value={selectedLoc}
                    onChange={(e) => setSelectedLoc(e.target.value)}
                    className="text-xs bg-white border border-border rounded-sm px-2 py-1.5 outline-none"
                  >
                    {locations.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                  </select>
                }>
            <ChartBlock spec={{ kind: "bar", x_key: "salesman_name", y_keys: ["net_value"] }} data={salesmanByLoc} height={320} />
            <div className="mt-3 text-xs text-muted-foreground">Contribution % of each salesman to this location's total sales.</div>
          </Card>
          <Card testId="salesman-avg-ticket-yoy" title="Salesman · 2025 vs 2026 · net sales + contribution">
            <div className="overflow-auto max-h-[500px] scrollbar-thin">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/95">
                  <tr className="text-left">
                    {["#", "Salesman", "Net 2025 (KWD)", "Net 2026 (KWD)", "Δ %", "Contribution 2026", "Avg Ticket 2026", "Home Location", "% of Home-Loc Target"].map((h) => (
                      <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold border-b hairline">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {salesmenYoy.map((r, i) => {
                    const n25 = r.net_value_2025 || 0;
                    const n26 = r.net_value_2026 || 0;
                    const delta = n25 ? ((n26 - n25) / n25 * 100) : null;
                    const contrib = r.contribution_pct_2026;
                    const fulfil = r.primary_location_fulfillment_pct;
                    return (
                      <tr key={r.salesman_code} className="border-b hairline hover:bg-myb-50/50" data-testid={`salesman-row-${r.salesman_code}`}>
                        <td className="py-2 px-3 text-muted-foreground">{i + 1}</td>
                        <td className="py-2 px-3">{r.salesman_name || <span className="text-amber-600">(missing)</span>}</td>
                        <td className="py-2 px-3 text-muted-foreground">{n25 ? fmtNumber(n25) : "—"}</td>
                        <td className="py-2 px-3 font-medium text-myb-500">{n26 ? fmtNumber(n26) : "—"}</td>
                        <td className={`py-2 px-3 text-xs font-medium ${delta == null ? "text-muted-foreground" : delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {delta == null ? "—" : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta).toFixed(1)}%`}
                        </td>
                        <td className="py-2 px-3 font-medium tabular-nums">
                          {contrib != null ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1 bg-myb-50 rounded-full overflow-hidden max-w-[80px]">
                                <div className="h-full bg-myb-500" style={{ width: `${Math.min(100, contrib * 4)}%` }} />
                              </div>
                              <span className="text-xs">{contrib.toFixed(2)}%</span>
                            </div>
                          ) : "—"}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{r.avg_ticket_2026 ? fmtNumber(r.avg_ticket_2026) : "—"}</td>
                        <td className="py-2 px-3">
                          {r.primary_location_name ? (
                            <div className="leading-tight">
                              <div className="text-xs font-medium">{r.primary_location_name}</div>
                              <div className="text-[10px] text-muted-foreground font-mono tabular-nums mt-0.5">
                                {r.primary_location_net ? fmtNumber(r.primary_location_net) : "—"} / {r.primary_location_target ? fmtNumber(r.primary_location_target) : "no target"}
                              </div>
                            </div>
                          ) : <span className="text-xs text-muted-foreground italic">—</span>}
                        </td>
                        <td className="py-2 px-3 tabular-nums" data-testid={`salesman-fulfil-${r.salesman_code}`}>
                          {fulfil == null ? (
                            <span className="text-xs text-muted-foreground italic">{r.primary_location_name ? "no target" : "—"}</span>
                          ) : (
                            <div className="flex items-center gap-2 max-w-[160px]">
                              <div className="flex-1 h-1.5 bg-myb-50 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${fulfil >= 25 ? "bg-emerald-500" : fulfil >= 10 ? "bg-amber-500" : "bg-rose-400"}`}
                                  style={{ width: `${Math.min(100, fulfil * 4)}%` }}
                                />
                              </div>
                              <span className={`text-xs font-medium ${fulfil >= 25 ? "text-emerald-700" : "text-foreground"}`}>{fulfil.toFixed(2)}%</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="skus" className="mt-5">
          <Card testId="top-skus-table" title="Top SKUs · filter by brand for per-brand top-10"
                right={<span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">2026-only (2025 SKU codes are inconsistent)</span>}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b hairline">
                  {["SKU", "Description", "Brand", "Qty", "Net (KWD)"].map((h) => (
                    <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topSkus.map((r, i) => (
                  <tr key={i} className="border-b hairline hover:bg-myb-50/50">
                    <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{r.sku}</td>
                    <td className="py-2 px-3">{r.name}</td>
                    <td className="py-2 px-3 text-myb-500">{r.brand}</td>
                    <td className="py-2 px-3 text-muted-foreground">{fmtNumber(r.qty)}</td>
                    <td className="py-2 px-3 font-medium">{fmtNumber(r.net_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
